import { useState } from "react";
import { supabase } from "../../lib/supabase";
import posthog from "../../lib/posthog";
import bouttimeLogo from "../../assets/bouttime-logo.svg";

const EyeIcon = ({ open, color }) => open ? (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
  </svg>
) : (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

export default function SignUpScreen({ onGoToSignIn, onGoToSignInWithData, appTheme }) {
  const [name, setName]         = useState("");
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm]   = useState("");
  const [showPw, setShowPw]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [emailExists, setEmailExists] = useState(false);

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "14px 16px",
    borderRadius: "14px",
    border: `1px solid ${appTheme.tealMid}`,
    background: appTheme.inputBackground,
    fontSize: "16px",
    color: appTheme.primaryText,
    outline: "none",
    fontFamily: appTheme.fontBody,
  };

  async function handleSignUp(e) {
    e.preventDefault();
    setError("");
    setEmailExists(false);

    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setLoading(true);
    const { data: authData, error: authErr } = await supabase.auth.signUp({ email, password });
    if (authErr) {
      setLoading(false);
      if (authErr.message.toLowerCase().includes("already")) {
        setEmailExists(true);
      } else {
        setError(authErr.message);
      }
      return;
    }

    // With email confirmation disabled, identities will always be populated for new users.
    // An empty identities array means the email already has an account.
    if (authData?.user?.identities?.length === 0) {
      setLoading(false);
      setEmailExists(true);
      return;
    }

    if (authData?.user) {
      await supabase.from("user_profiles").insert({
        id:                       authData.user.id,
        email,
        name:                     name.trim() || "",
        has_completed_onboarding: true,
      }).then(({ error: insertErr }) => {
        if (insertErr) console.error("[SignUp] user_profiles insert error:", insertErr.message);
      });
    }

    posthog.capture("user_signed_up");
    // Session fires automatically via onAuthStateChange in App.js → navigates to main app
  }

  return (
    <div style={{
      minHeight: "100vh",
      background: appTheme.pageBackground,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      padding: "52px 24px 48px",
    }}>
      {/* Small logo */}
      <img src={bouttimeLogo} alt="BoutTime" style={{ height: "36px", width: "auto", marginBottom: "32px" }} />

      <div style={{ width: "100%", maxWidth: "400px" }}>
        <div style={{ textAlign: "center", marginBottom: "28px" }}>
          <h1 style={{
            fontFamily: appTheme.fontHeading,
            fontSize: "28px",
            fontWeight: "600",
            color: appTheme.primaryText,
            margin: "0 0 8px",
          }}>
            Create your account
          </h1>
          <p style={{ margin: 0, fontFamily: appTheme.fontBody, fontSize: "15px", color: appTheme.secondaryText }}>
            Save your targets and start planning.
          </p>
        </div>

        <form onSubmit={handleSignUp} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {/* Name — optional */}
          <input
            type="text"
            value={name}
            onChange={e => {
              const v = e.target.value;
              setName(v.length === 1 ? v.toUpperCase() : v);
            }}
            placeholder="Your name"
            autoComplete="name"
            style={inputStyle}
          />

          {/* Email */}
          <div>
            <input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setEmailExists(false); }}
              placeholder="Email address"
              required={!emailExists}
              style={inputStyle}
            />
            {emailExists && (
              <div style={{
                marginTop: "10px",
                padding: "14px 16px",
                borderRadius: "14px",
                background: "rgba(250,243,232,0.95)",
                border: `1px solid ${appTheme.tealMid}`,
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}>
                <p style={{
                  margin: 0,
                  fontFamily: appTheme.fontBody,
                  fontSize: "14px",
                  color: appTheme.primaryText,
                  lineHeight: 1.5,
                }}>
                  Looks like you already have an account with this email.
                </p>
                <button
                  type="button"
                  onClick={onGoToSignInWithData}
                  style={{
                    width: "100%",
                    padding: "13px 16px",
                    borderRadius: "12px",
                    border: "none",
                    background: appTheme.accentColor,
                    color: "#fff",
                    fontFamily: appTheme.fontBody,
                    fontWeight: "700",
                    fontSize: "15px",
                    cursor: "pointer",
                    touchAction: "manipulation",
                  }}
                >
                  Sign in instead
                </button>
                <button
                  type="button"
                  onClick={() => { setEmail(""); setEmailExists(false); }}
                  style={{
                    width: "100%",
                    padding: "13px 16px",
                    borderRadius: "12px",
                    border: `1px solid ${appTheme.tealMid}`,
                    background: "transparent",
                    color: appTheme.secondaryText,
                    fontFamily: appTheme.fontBody,
                    fontWeight: "600",
                    fontSize: "15px",
                    cursor: "pointer",
                    touchAction: "manipulation",
                  }}
                >
                  Use a different email
                </button>
              </div>
            )}
          </div>

          {/* Password + confirm — hidden while email-exists prompt is showing */}
          {!emailExists && (
            <>
              <div style={{ position: "relative" }}>
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Password (min 6 characters)"
                  required
                  minLength={6}
                  style={{ ...inputStyle, paddingRight: "46px" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(p => !p)}
                  style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", touchAction: "manipulation" }}
                >
                  <EyeIcon open={showPw} color={appTheme.mutedText} />
                </button>
              </div>

              <div style={{ position: "relative" }}>
                <input
                  type={showConfirm ? "text" : "password"}
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder="Confirm password"
                  required
                  style={{ ...inputStyle, paddingRight: "46px" }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(p => !p)}
                  style={{ position: "absolute", right: "14px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", touchAction: "manipulation" }}
                >
                  <EyeIcon open={showConfirm} color={appTheme.mutedText} />
                </button>
              </div>

              {error && (
                <p style={{ margin: 0, fontFamily: appTheme.fontBody, fontSize: "13px", color: "#dc2626", textAlign: "center" }}>
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "16px",
                  borderRadius: "14px",
                  border: "none",
                  background: appTheme.accentColor,
                  color: "#fff",
                  fontFamily: appTheme.fontBody,
                  fontWeight: "700",
                  fontSize: "16px",
                  cursor: loading ? "not-allowed" : "pointer",
                  opacity: loading ? 0.7 : 1,
                  touchAction: "manipulation",
                  marginTop: "4px",
                }}
              >
                {loading ? "Creating account…" : "Create account"}
              </button>

              <p style={{ margin: "4px 0 0", textAlign: "center", fontFamily: appTheme.fontBody, fontSize: "14px", color: appTheme.secondaryText }}>
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={onGoToSignInWithData}
                  style={{ background: "none", border: "none", cursor: "pointer", color: appTheme.accentColor, fontWeight: "700", fontSize: "14px", padding: 0, fontFamily: appTheme.fontBody, touchAction: "manipulation" }}
                >
                  Sign in
                </button>
              </p>
            </>
          )}

          <p style={{ margin: "8px 0 0", textAlign: "center", fontFamily: appTheme.fontBody, fontSize: "12px", color: appTheme.mutedText, lineHeight: 1.5 }}>
            By signing up you agree to our{" "}
            <button
              type="button"
              onClick={() => window.open("https://bouttime.app/terms", "_blank")}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: appTheme.accentColor, fontFamily: appTheme.fontBody, fontSize: "12px", fontWeight: "600", textDecoration: "none", touchAction: "manipulation" }}
            >
              Terms of Service
            </button>
            {" "}and{" "}
            <button
              type="button"
              onClick={() => window.open("https://bouttime.app/privacy", "_blank")}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: appTheme.accentColor, fontFamily: appTheme.fontBody, fontSize: "12px", fontWeight: "600", textDecoration: "none", touchAction: "manipulation" }}
            >
              Privacy Policy
            </button>
            .
          </p>
        </form>
      </div>
    </div>
  );
}
